import { Component, Inject } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MODAL_WIDTHS } from '../../../../domain/types/notification';

export interface TemplateDiffData {
  versionNumber: number;
  oldSubject: string;
  newSubject: string;
  oldBody: string;
  newBody: string;
}

interface DiffLine {
  type: 'context' | 'add' | 'remove';
  text: string;
}

@Component({
  selector: 'template-diff-modal',
  templateUrl: './template-diff-modal.component.html',
  styleUrls: ['./template-diff-modal.component.scss']
})
export class TemplateDiffModalComponent {
  data: TemplateDiffData;
  subjectChanged: boolean;
  bodyDiff: DiffLine[];

  constructor(
    private readonly dialogRef: MatDialogRef<TemplateDiffModalComponent>,
    @Inject(MAT_DIALOG_DATA) data: TemplateDiffData
  ) {
    this.data = data;
    this.subjectChanged = data.oldSubject !== data.newSubject;
    this.bodyDiff = TemplateDiffModalComponent.diffLines(data.oldBody, data.newBody);
  }

  /** Unified line diff via longest-common-subsequence — no external library. */
  private static diffLines(oldText: string, newText: string): DiffLine[] {
    const a = (oldText || '').split('\n');
    const b = (newText || '').split('\n');
    const m = a.length;
    const n = b.length;
    const lcs: number[][] = Array.from({ length: m + 1 }, () => new Array<number>(n + 1).fill(0));
    for (let i = m - 1; i >= 0; i--) {
      for (let j = n - 1; j >= 0; j--) {
        lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
      }
    }
    const out: DiffLine[] = [];
    let i = 0;
    let j = 0;
    while (i < m && j < n) {
      if (a[i] === b[j]) {
        out.push({ type: 'context', text: a[i] });
        i++;
        j++;
      } else if (lcs[i + 1][j] >= lcs[i][j + 1]) {
        out.push({ type: 'remove', text: a[i] });
        i++;
      } else {
        out.push({ type: 'add', text: b[j] });
        j++;
      }
    }
    while (i < m) { out.push({ type: 'remove', text: a[i] }); i++; }
    while (j < n) { out.push({ type: 'add', text: b[j] }); j++; }
    return out;
  }

  public static open(md: MatDialog, data: TemplateDiffData): MatDialogRef<TemplateDiffModalComponent> {
    return md.open(TemplateDiffModalComponent, { width: MODAL_WIDTHS.wide, data });
  }
}
